import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { merge, reduce } from 'lodash/fp';

import { convertToMultiCurrencyAmount } from '@mews-distributor/common';
import { CancellationFee, CancellationFeeResult, ServicePricingResult } from '@mews-distributor/data-access';
import { keyBy, values } from '@mews/fp';

import { Metadata } from '../../types/Metadata';
import { PricingHashes, RatePricing, ResourceCategoryPricing, WithPricingHashes } from '../servicePricing';
import { Pricing, PricingRates, PricingValue } from './types';

const ratePricingToMultiCurrencyRatesPricing = ({
    totalAmount,
    averageAmountPerTimeUnit,
    averageAmountPerFullTimeUnit,
}: RatePricing) => ({
    totalAmount: convertToMultiCurrencyAmount(totalAmount),
    averageAmountPerTimeUnit: convertToMultiCurrencyAmount(averageAmountPerTimeUnit),
    averageAmountPerFullTimeUnit: convertToMultiCurrencyAmount(averageAmountPerFullTimeUnit),
});

const getInitialPricingValues = (pricingHashes: Record<string, string>) =>
    pipe(
        pricingHashes,
        values,
        array.map<string, [string, PricingValue]>((pricingHash) => [
            pricingHash,
            { isFetching: false, hasFailed: false, rates: {} },
        ]),
        Object.fromEntries<PricingValue>
    );

export const getApplicableCancellationFees =
    (rateId: string, categoryId: string) => (policies: CancellationFeeResult[]) =>
        pipe(
            option.fromNullable(policies),
            option.flatMap(array.findFirst((policy) => policy.rateId === rateId && policy.categoryId === categoryId)),
            option.map((policy) => policy.fees),
            option.getOrElse<CancellationFee[]>(() => [])
        );

export const servicePricingToPricing = ({
    categoryPrices,
    pricingHashes,
    cancellationFees,
}: Pick<ServicePricingResult, 'categoryPrices' | 'cancellationFees'> & WithPricingHashes): Pricing =>
    pipe(
        categoryPrices,
        array.reduce<ResourceCategoryPricing, Pricing>(
            getInitialPricingValues(pricingHashes),
            (acc, { categoryId, occupancyPrices }) => {
                const firstOccupancyPrice = occupancyPrices[0];

                if (firstOccupancyPrice == null) {
                    return acc;
                }
                const { rateGroupPrices } = firstOccupancyPrice;
                const rates: PricingRates = pipe(
                    rateGroupPrices,
                    array.map(
                        ({
                            minRateId: id,
                            maxPrice,
                            minPrice,
                            cityTaxIncludedInPrice,
                            sellLimitStatistic,
                            feeProducts,
                        }) => ({
                            id,
                            maxPrice: ratePricingToMultiCurrencyRatesPricing(maxPrice),
                            price: ratePricingToMultiCurrencyRatesPricing(minPrice),
                            cityTaxIncludedInPrice,
                            cancellationFees: getApplicableCancellationFees(id, categoryId)(cancellationFees),
                            sellLimitStatistic,
                            feeProducts,
                        })
                    ),
                    (rateCollection) =>
                        array.isNonEmpty(rateCollection) ? option.some(keyBy('id', rateCollection)) : option.none,
                    option.getOrElse<PricingRates>(() => ({}))
                );

                const pricingHash = pricingHashes[categoryId];
                if (pricingHash != null) {
                    acc[pricingHash] = {
                        isFetching: false,
                        hasFailed: false,
                        rates,
                    };
                }

                return acc;
            }
        )
    );

const setPricingHashes = (metadata: Metadata) => (state: Pricing, pricingHashes: PricingHashes) =>
    reduce(
        (acc, hash) =>
            merge(acc, {
                [hash]: { ...metadata, rates: {} },
            }),
        state,
        pricingHashes
    );

export const setFailedPricingHashes = setPricingHashes({ isFetching: false, hasFailed: true });
export const setStartedPricingHashes = setPricingHashes({ isFetching: true, hasFailed: false });
